import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * A string kept in the URL query (search, filters, sorting), so back,
 * reload and sharing restore the list view (docs/ux-richtlinien.md §6.3).
 * The default value is not written to the URL.
 */
export function useUrlState<T extends string = string>(
  key: string,
  defaultValue: NoInfer<T>,
  allowed?: readonly NoInfer<T>[],
): [T, (value: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value =
    raw !== null && (!allowed || allowed.includes(raw as T)) ? (raw as T) : defaultValue;
  const setValue = useCallback(
    (next: T) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next === defaultValue || next === '') updated.delete(key);
          else updated.set(key, next);
          return updated;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setParams],
  );
  return [value, setValue];
}

/** Removes the given query parameters at once, e.g. "Filter zurücksetzen". */
export function useResetUrlState(keys: readonly string[]): () => void {
  const [, setParams] = useSearchParams();
  return useCallback(() => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        for (const key of keys) updated.delete(key);
        return updated;
      },
      { replace: true },
    );
  }, [keys, setParams]);
}

/** Sets or removes several query parameters in one update (empty value removes). */
export function useSetUrlStates(): (patch: Record<string, string>) => void {
  const [, setParams] = useSearchParams();
  return useCallback(
    (patch) =>
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === '') updated.delete(key);
            else updated.set(key, value);
          }
          return updated;
        },
        { replace: true },
      ),
    [setParams],
  );
}
